import {setTitle as setDocumentTitle} from '@github-ui/document-metadata'
import type React from 'react'
import {useEffect, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, useFragment} from 'react-relay'
import {usePreloadedQuery} from 'react-relay/hooks'

import type {Header$key} from './__generated__/Header.graphql'
import type {HeaderQuery} from './__generated__/HeaderQuery.graphql'
import {HeaderEditor} from './HeaderEditor'
import {HeaderViewer} from './HeaderViewer'
import {Box, Flash, IconButton} from '@primer/react'
import {HeaderMetadata} from './HeaderMetadata'
import {ObservableBox} from '@github-ui/use-sticky-header/ObservableBox'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {ContentWrapper} from '../ContentWrapper'
import {XIcon} from '@primer/octicons-react'
import type {OptionConfig} from '../OptionConfig'

export const HeaderGraphqlQuery = graphql`
  query HeaderQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...Header
      }
    }
  }
`

type HeaderBaseProps = {
  optionConfig: OptionConfig
  metadataPaneTrigger: JSX.Element
}

export type HeaderFetchedProps = HeaderBaseProps & {
  issuesQueryRef: PreloadedQuery<HeaderQuery>
}

type HeaderProps = HeaderBaseProps & {
  issue: Header$key
}

export function HeaderFetched({issuesQueryRef, ...rest}: HeaderFetchedProps) {
  const preloadedData = usePreloadedQuery<HeaderQuery>(HeaderGraphqlQuery, issuesQueryRef)
  return preloadedData.repository && preloadedData.repository.issue ? (
    <Header issue={preloadedData.repository.issue} {...rest} />
  ) : null
}

export function Header({issue, optionConfig, metadataPaneTrigger}: HeaderProps) {
  const data = useFragment(
    graphql`
      fragment Header on Issue {
        title
        number
        id
        isTransferInProgress
        repository {
          nameWithOwner
          id
        }
        ...HeaderViewer
        ...HeaderMetadata
      }
    `,
    issue,
  )

  const {
    id,
    title: issueTitle,
    number,
    repository: {nameWithOwner},
  } = data

  const [title, setTitle] = useState<string>(issueTitle)
  const titleRef = useRef<HTMLInputElement>(null)
  const [isIssueTitleEditActive, setIsIssueTitleEditActive] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const shouldUpdateDocumentTitle = optionConfig.selectedProjectId === undefined
  useEffect(() => {
    if (issueTitle && shouldUpdateDocumentTitle) {
      setDocumentTitle(`${title} · Issue #${number} · ${nameWithOwner}`)
    }
  }, [shouldUpdateDocumentTitle, issueTitle, nameWithOwner, number, title])

  useEffect(() => {
    if (isIssueTitleEditActive) return
    setTitle(issueTitle)
  }, [isIssueTitleEditActive, issueTitle, setTitle])

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = title
    const newValue = e.target.value
    if (newValue !== null && newValue !== oldValue) {
      setTitle(newValue)
    }
  }

  // Focus on title when user clicks on edit
  useEffect(() => {
    if (isIssueTitleEditActive && titleRef.current) {
      titleRef.current.focus()
    }
  }, [isIssueTitleEditActive])

  const {isSticky, stickyStyles, observe, unobserve} = useStickyHeader()
  const [showTransferWarning, setShowTransferWarning] = useState(data.isTransferInProgress)

  return (
    <>
      <ContentWrapper sx={optionConfig.innerSx}>
        {showTransferWarning && (
          <Flash
            aria-live="polite"
            sx={{mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
          >
            Issue transfer in progress
            <IconButton
              variant="invisible"
              aria-label="Dismiss issue is in transfer notification"
              icon={XIcon}
              onClick={() => setShowTransferWarning(false)}
            />
          </Flash>
        )}
        {isIssueTitleEditActive || isSubmitting ? (
          <HeaderEditor
            issueId={id}
            title={title}
            titleRef={titleRef}
            onTitleChange={onTitleChange}
            onIssueUpdate={optionConfig.onIssueUpdate}
            cancelIssueTitleEdit={() => {
              setIsSubmitting(false)
              setIsIssueTitleEditActive(false)
            }}
            isDirty={issueTitle !== title}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        ) : (
          <HeaderViewer
            headerViewerKey={data}
            optionConfig={optionConfig}
            setIsIssueTitleEditActive={setIsIssueTitleEditActive}
            isSticky={isSticky}
            metadataPaneTrigger={metadataPaneTrigger}
          />
        )}
      </ContentWrapper>
      <ObservableBox
        sx={{top: '-1px', height: '1px', visibility: 'hidden'}}
        onObserve={observe}
        onUnobserve={unobserve}
      />
      <HeaderMetadata
        headerMetadataKey={data}
        optionConfig={optionConfig}
        isSticky={isSticky}
        setIsIssueTitleEditActive={setIsIssueTitleEditActive}
        metadataPaneTrigger={metadataPaneTrigger}
        stickyStyles={stickyStyles}
      />
      <ContentWrapper sx={{mb: 3, ...optionConfig.innerSx}}>
        <Box
          sx={{
            width: '100%',
            marginBottom: 3,
          }}
        />
      </ContentWrapper>
    </>
  )
}

try{ HeaderFetched.displayName ||= 'HeaderFetched' } catch {}
try{ Header.displayName ||= 'Header' } catch {}